/**
 * @file tournamentService.ts
 * @description Tournament API Service with TypeScript - Task 18.5
 *
 * Provides typed methods for tournament CRUD operations:
 * - createTournament: Create a new tournament
 * - joinTournament: Join an existing tournament (with payment support)
 * - getTournamentDetails: Get tournament details
 * - getTournamentRanking: Get tournament leaderboard
 *
 * Reference: .taskmaster/docs/research/task-18-api-services-typescript-migration.md
 * Section 3.5
 */

import apiClient from './apiClient';
import { mapApiErrorToUIState } from './paymentService';
import type {
  TournamentResponse,
  TournamentRankingResponse,
  CreateTournamentParams,
  TournamentResponseDataBase,
} from '../types/tournament';
import type { JoinTournamentResponse } from '../types/payment';

type TournamentId = string | number;

const formatTournamentId = (id: TournamentId): string => String(id);

const ensureTournamentId = (id: TournamentId): void => {
  if (id === undefined || id === null || `${id}`.trim() === '') {
    throw new Error('Tournament ID is required');
  }
};

/**
 * Response type for cancel tournament endpoint
 */
interface CancelTournamentResponse {
  data: {
    id: number | string;
    attributes?: {
      status?: string;
    };
    status?: string;
    message?: string;
    refundRecordsCreated?: number;
  };
}

/**
 * Cancels a tournament before it starts.
 * Requires the current user to be the owner or an admin.
 *
 * @param tournamentId - The ID of the tournament to cancel
 * @returns CancelTournamentResponse with updated status
 */
export const cancelTournament = async (
  tournamentId: TournamentId,
): Promise<CancelTournamentResponse> => {
  try {
    ensureTournamentId(tournamentId);
    const response = await apiClient.post<CancelTournamentResponse>(
      `/tournaments/${formatTournamentId(tournamentId)}/cancel`,
    );
    return response.data;
  } catch (error) {
    const axiosError = error as { response?: { data?: unknown }; message?: string };
    throw axiosError.response ? axiosError.response.data : axiosError.message;
  }
};

/**
 * Extended payload for create tournament API
 * Includes CreateTournamentParams plus any additional fields
 */
export type CreateTournamentPayload = CreateTournamentParams & Record<string, unknown>;

/**
 * Creates a new tournament with the specified parameters.
 *
 * @param tournamentData - The tournament configuration
 * @returns Typed TournamentResponse with created tournament data
 */
export const createTournament = async (
  tournamentData: CreateTournamentPayload,
): Promise<TournamentResponse> => {
  try {
    const response = await apiClient.post<TournamentResponse>('/tournaments/', {
      data: tournamentData,
    });
    return response.data;
  } catch (error) {
    const axiosError = error as { response?: { data?: unknown }; message?: string };
    throw axiosError.response ? axiosError.response.data : axiosError.message;
  }
};

/**
 * Joins a tournament with the provided tournament ID and optional hash.
 *
 * @param tournamentId - The ID of the tournament to join
 * @param hash - Optional hash for joining private tournaments
 * @param paymentId - Optional payment ID for paid tournaments
 * @param kucoinConsent - Optional KuCoin EU marketing consent (tournament 299 only); sent only when ticked
 * @returns Typed JoinTournamentResponse with join status
 */
export const joinTournament = async (
  tournamentId: TournamentId,
  hash?: string,
  paymentId?: string,
  kucoinConsent?: boolean,
): Promise<JoinTournamentResponse> => {
  try {
    if (paymentId) {
      const response = await apiClient.post<JoinTournamentResponse>(
        `/tournaments/${formatTournamentId(tournamentId)}/join`,
        { paymentId },
      );
      return response.data;
    }

    const url = hash?.trim()
      ? `/tournaments/${formatTournamentId(tournamentId)}/join?hash=${hash}`
      : `/tournaments/${formatTournamentId(tournamentId)}/join`;
    const response = kucoinConsent
      ? await apiClient.put<JoinTournamentResponse>(url, { kucoinConsent: true })
      : await apiClient.put<JoinTournamentResponse>(url);
    return response.data;
  } catch (error) {
    const paymentUiState = mapApiErrorToUIState(error);
    if (paymentUiState) {
      const axiosError = error as { response?: { status?: number } };
      throw {
        uiState: paymentUiState,
        status: axiosError.response?.status,
        rawError: error,
      };
    }

    throw (error as { response?: { data?: unknown } }).response?.data ?? error;
  }
};

/**
 * Response type for ready tournament endpoint
 */
interface ReadyTournamentResponse {
  data: {
    id: number | string;
    attributes: {
      status: string;
    };
  };
}

/**
 * Marks the user as ready for a tournament.
 *
 * @param tournamentId - The ID of the tournament
 * @returns Ready status response
 */
export const readyTournament = async (
  tournamentId: TournamentId,
): Promise<ReadyTournamentResponse> => {
  try {
    const response = await apiClient.put<ReadyTournamentResponse>(
      `/tournaments/${formatTournamentId(tournamentId)}/ready`,
    );
    return response.data;
  } catch (error) {
    const axiosError = error as { response?: { data?: unknown }; message?: string };
    throw axiosError.response ? axiosError.response.data : axiosError.message;
  }
};

/**
 * Response type for tournament list endpoint
 */
interface TournamentListResponse {
  data: Array<{
    id: number | string;
    // TEAM_003: Use the common base shape for tournament details. The list
    // endpoint still returns Strapi-like { id, attributes } items, but the
    // attributes payload matches TournamentResponseDataBase.
    attributes: TournamentResponseDataBase;
  }>;
}

/**
 * Retrieves the list of all tournaments.
 *
 * @returns List of tournaments
 */
export const getAllTournaments = async (): Promise<TournamentListResponse> => {
  try {
    const response = await apiClient.get<TournamentListResponse>('/tournaments');
    return response.data;
  } catch (error) {
    const axiosError = error as { response?: { data?: unknown }; message?: string };
    throw axiosError.response ? axiosError.response.data : axiosError.message;
  }
};

/**
 * Retrieves the details of a specific tournament.
 *
 * @param tournamentID - The ID of the tournament
 * @returns Typed TournamentResponse with tournament details
 */
export const getTournamentDetails = async (
  tournamentID: TournamentId,
): Promise<TournamentResponse> => {
  try {
    ensureTournamentId(tournamentID);
    const response = await apiClient.get<TournamentResponse>(
      // TEAM_003: Backend now ignores populate and returns a flattened Strapi response
      // for GET /api/tournaments/:id with all required relations (participants,
      // market_type.crypto_trading_pairs, currentRanking, etc.) already populated.
      // We therefore call /tournaments/:id without any populate query params.
      `/tournaments/${formatTournamentId(tournamentID)}`,
    );
    return response.data;
  } catch (error) {
    const axiosError = error as { response?: { data?: unknown }; message?: string };
    throw axiosError.response ? axiosError.response.data : axiosError.message;
  }
};

/**
 * Balance entry for tournament
 */
interface TournamentBalance {
  id: number;
  attributes: {
    amount: string;
    currency: string;
    tournament?: {
      data: { id: number };
    };
    user?: {
      data: { id: number; attributes: { username: string } };
    };
  };
}

/**
 * Response type for tournament balances endpoint
 */
interface TournamentBalancesResponse {
  data: TournamentBalance[];
}

/**
 * Retrieves the balances of a specific tournament.
 *
 * @param tournamentID - The ID of the tournament
 * @returns Tournament balances with user information
 */
export const getTournamentBalances = async (
  tournamentID: TournamentId,
): Promise<TournamentBalancesResponse> => {
  try {
    ensureTournamentId(tournamentID);
    const response = await apiClient.get<TournamentBalancesResponse>(
      `/balances?filters[tournament][id]=${formatTournamentId(
        tournamentID,
      )}&populate=tournament,user`,
    );
    return response.data;
  } catch (error) {
    const axiosError = error as { response?: { data?: unknown }; message?: string };
    throw axiosError.response ? axiosError.response.data : axiosError.message;
  }
};

/**
 * Response type for crypto trading pair details
 */
interface CryptoTradingPairResponse {
  data: {
    id: number;
    attributes: {
      symbol: string;
      baseAsset: string;
      quoteAsset: string;
    };
  };
}

/**
 * Retrieves the details of a specific crypto trading pair.
 *
 * @param pairId - The ID of the crypto trading pair
 * @returns Crypto trading pair details or undefined if no ID
 */
export const getCryptoTradingPairDetails = async (
  pairId: TournamentId,
): Promise<CryptoTradingPairResponse | undefined> => {
  if (!pairId && pairId !== 0) {
    return undefined;
  }

  try {
    const response = await apiClient.get<CryptoTradingPairResponse>(
      `/crypto-trading-pairs/${formatTournamentId(pairId)}?populate=*`,
    );
    return response.data;
  } catch (error) {
    const axiosError = error as { response?: { data?: unknown }; message?: string };
    throw axiosError.response ? axiosError.response.data : axiosError.message;
  }
};

/**
 * Crypto trading pair in list response
 * Task 39.3: Exported for use in TournamentContext
 *
 * TEAM_003: Backend now returns market_type as a dynamic zone with
 * `crypto_trading_pairs` already populated as a flat array, where each
 * pair has `id`, `name`, `base.short_name`, `quote.short_name`, etc.
 *
 * To minimise churn in existing components (WrapAsset, GraphBox, useSocket),
 * we keep a Strapi-like shape with an `attributes` object exposing a
 * `name` field so that code using `coin.attributes.name` continues to work.
 */
export interface CryptoTradingPairListItem {
  id: number;
  attributes: {
    /** Human readable pair name, e.g. 'BTC/USDT' */
    name: string;
    /** Base asset short name, e.g. 'BTC' */
    baseShortName?: string;
    /** Quote asset short name, e.g. 'USDT' */
    quoteShortName?: string;
  };
}

/**
 * Retrieves the details of crypto trading pair list.
 *
 * @param tournamentID - The ID of the tournament
 * @returns List of crypto trading pairs for the tournament
 */
export const getCryptoTradingPairList = async (
  tournamentID: TournamentId,
): Promise<CryptoTradingPairListItem[]> => {
  try {
    ensureTournamentId(tournamentID);

    const response = await apiClient.get<{
      data?: {
        id: number;
        // TEAM_003: Support both legacy { attributes: { market_type } } and
        // new flattened { market_type } shapes to allow gradual rollout.
        attributes?: {
          market_type?: Array<any>;
        };
        market_type?: Array<any>;
      };
    }>(`/tournaments/${formatTournamentId(tournamentID)}`);

    const rawData = response.data?.data as
      | { attributes?: { market_type?: Array<any> } }
      | { market_type?: Array<any> }
      | undefined;

    const marketTypes: Array<any> | undefined =
      (rawData as any)?.attributes?.market_type ?? (rawData as any)?.market_type;

    if (!Array.isArray(marketTypes) || marketTypes.length === 0) {
      // No market_type configured for this tournament yet (e.g., newly created).
      // Frontend should treat this as "no pairs available" instead of throwing.
      return [];
    }

    const cryptoMarket = marketTypes.find(
      (m: any) => m?.__component === 'market-types.crypto',
    );

    if (!cryptoMarket || !Array.isArray(cryptoMarket.crypto_trading_pairs)) {
      return [];
    }

    const pairs = cryptoMarket.crypto_trading_pairs as Array<{
      id: number;
      name?: string;
      base?: { id?: number; short_name?: string };
      quote?: { id?: number; short_name?: string };
    }>;

    return pairs.map((pair) => ({
      id: pair.id,
      attributes: {
        name: pair.name ?? '',
        baseShortName: pair.base?.short_name,
        quoteShortName: pair.quote?.short_name,
      },
    }));
  } catch (error) {
    const axiosError = error as { response?: { data?: unknown }; message?: string };
    throw axiosError.response ? axiosError.response.data : axiosError.message;
  }
};

/**
 * Retrieves the ranking of a specific tournament.
 *
 * @param tournamentID - The ID of the tournament
 * @returns Tournament ranking with player data
 */
export const getTournamentRanking = async (
  tournamentID: TournamentId,
): Promise<TournamentRankingResponse> => {
  try {
    ensureTournamentId(tournamentID);

    const response = await apiClient.get<TournamentRankingResponse>(
      `/tournaments/${formatTournamentId(tournamentID)}/ranking`,
    );

    return response.data;
  } catch (error) {
    const axiosError = error as { response?: { data?: unknown }; message?: string };
    throw axiosError.response ? axiosError.response.data : axiosError.message;
  }
};

/**
 * Margin asset entry
 */
interface MarginAsset {
  id: number;
  symbol: string;
  name: string;
  available: boolean;
}

/**
 * Response type for tournament margin assets
 */
type TournamentMarginAssetsResponse = string[];

/**
 * Retrieves the margin assets of a specific tournament.
 *
 * @param tournamentID - The ID of the tournament
 * @returns List of margin assets available for the tournament
 */
export const getTournamentMarginAssets = async (
  tournamentID: TournamentId,
): Promise<TournamentMarginAssetsResponse> => {
  try {
    ensureTournamentId(tournamentID);
    const response = await apiClient.get<TournamentMarginAssetsResponse>(
      `/tournaments/${formatTournamentId(tournamentID)}/margin/assets`,
    );
    return response.data;
  } catch (error) {
    const axiosError = error as { response?: { data?: unknown }; message?: string };
    throw axiosError.response ? axiosError.response.data : axiosError.message;
  }
};
